import { useEffect, useState } from 'react';
import { Cross, MapPin, Phone, Navigation, Loader2, X, PackageCheck, ShoppingCart } from 'lucide-react';
import { products as productsApi } from '../lib/api';
import { useCart } from '../lib/cart';
import { toast } from 'sonner';

/**
 * Nearby Pharmacy Alert
 *
 * Shows the 3 nearest pharmacies within 5 km that have the medical item in
 * stock. Rendered inline when a medical product is out of stock in the
 * current store.
 *
 * Props:
 *   productId : the out-of-stock product's ID
 *   productName : (optional) plain display name for the header
 */
export default function NearbyPharmacyAlert({ productId, productName }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [coords, setCoords] = useState(null); // {lat,lng} once GPS resolves
  const cart = useCart();

  const orderFromNearby = (row) => {
    // Cross-village auto-fulfilment: replace cart with the alternative
    // pharmacy's version of the same product. `addItem` already handles
    // the store switch when carts differ.
    const store = { ...row.store };
    const product = {
      id: row.product_id,
      name: row.name,
      price: row.price,
      unit: row.unit,
      image_url: row.image_url,
      in_stock: row.in_stock,
    };
    cart.addItem(store, product);
    toast.success(`Added to cart from ${row.store.name}`, {
      description: `Cart switched to nearby pharmacy · ₹${row.price} / ${row.unit}`,
    });
    setOpen(false);
  };

  useEffect(() => {
    if (!open || data || loading) return;
    setLoading(true); setError('');

    const fetchAlternatives = (lat, lng) => {
      const params = { radius_km: 5, limit: 3 };
      if (lat != null && lng != null) { params.lat = lat; params.lng = lng; }
      productsApi.nearbyAlternatives(productId, params)
        .then((r) => setData(r))
        .catch(() => setError('Could not fetch nearby pharmacies right now.'))
        .finally(() => setLoading(false));
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setCoords(c);
          fetchAlternatives(c.lat, c.lng);
        },
        () => fetchAlternatives(null, null),
        { timeout: 5000, maximumAge: 60_000 }
      );
    } else {
      fetchAlternatives(null, null);
    }
  }, [open, productId, data, loading]);

  if (!open) {
    return (
      <button
        data-testid={`nearby-pharmacy-alert-btn-${productId}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#FFEBEE] hover:bg-[#FFCDD2] text-[#B71C1C] px-3 h-8 text-xs font-bold uppercase tracking-wider border border-[#FFCDD2] transition"
      >
        <Cross className="w-3.5 h-3.5"/>
        Find nearby pharmacy
      </button>
    );
  }

  return (
    <div
      data-testid={`nearby-pharmacy-alert-panel-${productId}`}
      onClick={(e) => e.stopPropagation()}
      className="mt-3 rounded-xl border border-[#FFCDD2] bg-[#FFF7F7] p-3 relative"
    >
      <button
        data-testid={`nearby-pharmacy-close-${productId}`}
        onClick={() => setOpen(false)}
        className="absolute top-2 right-2 text-ink/40 hover:text-ink"
        aria-label="Close nearby pharmacy panel"
      >
        <X className="w-4 h-4"/>
      </button>
      <div className="flex items-center gap-2">
        <Cross className="w-4 h-4 text-[#B71C1C]"/>
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#B71C1C]">
          Nearby pharmacies
        </div>
      </div>
      <div className="mt-0.5 text-sm text-ink/80">
        Out of stock here. Showing the 3 nearest pharmacies within 5 km that have
        {productName ? <> <b>{productName}</b></> : ' this item'} in stock.
        {!coords && !loading && (
          <span className="ml-1 text-[10px] text-ink/50">(using store location — allow GPS for exact radius)</span>
        )}
      </div>

      {loading && (
        <div className="mt-3 inline-flex items-center gap-2 text-xs text-ink/60">
          <Loader2 className="w-3.5 h-3.5 animate-spin"/> Searching nearby pharmacies…
        </div>
      )}

      {error && (
        <div className="mt-3 text-xs text-[#B71C1C]">{error}</div>
      )}

      {!loading && !error && data && data.count === 0 && (
        <div data-testid={`nearby-pharmacy-empty-${productId}`} className="mt-3 text-sm text-ink/70">
          No pharmacies within 5 km currently have this medicine in stock.
          You can tap <b>Notify me</b> above and we&apos;ll ping you the moment it&apos;s back.
        </div>
      )}

      {!loading && !error && data && data.count > 0 && (
        <ul data-testid={`nearby-pharmacy-list-${productId}`} className="mt-3 space-y-2">
          {data.results.map((r) => (
            <li
              key={r.product_id}
              data-testid={`nearby-pharmacy-item-${r.store.id}`}
              className="rounded-lg bg-white border border-[#FFE0E0] p-3 flex items-center gap-3"
            >
              <div className="w-10 h-10 rounded-lg bg-[#FFEBEE] grid place-items-center shrink-0">
                <Cross className="w-5 h-5 text-[#B71C1C]"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-ink text-sm line-clamp-1">{r.store.name}</div>
                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-ink/60">
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{r.store.village || r.store.pincode || '—'}</span>
                  {r.distance_km != null && (
                    <span data-testid={`nearby-pharmacy-distance-${r.store.id}`} className="inline-flex items-center gap-1 font-bold text-[#B71C1C]">
                      <Navigation className="w-3 h-3"/>{r.distance_km} km
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 text-[#1B5E20] font-bold">
                    <PackageCheck className="w-3 h-3"/>In stock · ₹{r.price}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-3 flex-wrap">
                  {r.store.phone && (
                    <a
                      data-testid={`nearby-pharmacy-call-${r.store.id}`}
                      href={`tel:${r.store.phone}`}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0D47A1] hover:underline"
                    >
                      <Phone className="w-3 h-3"/>{r.store.phone}
                    </a>
                  )}
                  <a
                    data-testid={`nearby-pharmacy-visit-${r.store.id}`}
                    href={`/stores/${r.store.id}`}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#D35400] hover:underline"
                  >
                    Visit shop →
                  </a>
                  {r.store.lat != null && r.store.lng != null && (
                    <a
                      data-testid={`nearby-pharmacy-map-${r.store.id}`}
                      href={`https://www.google.com/maps?q=${r.store.lat},${r.store.lng}`}
                      target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-ink/60 hover:text-ink"
                    >
                      <MapPin className="w-3 h-3"/>Directions
                    </a>
                  )}
                </div>
              </div>
              <button
                data-testid={`nearby-pharmacy-order-${r.store.id}`}
                onClick={() => orderFromNearby(r)}
                className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[#1B5E20] hover:bg-[#0D3F12] text-white px-3 h-8 text-[11px] font-bold uppercase tracking-wider transition"
                title={`Add ₹${r.price} ${r.name} from ${r.store.name} to your cart`}
              >
                <ShoppingCart className="w-3 h-3"/>Order here
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
